import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import api from '../lib/api';
import useVoiceInput from '../hooks/useVoiceInput';
import { parseBriefyResponse, briefyActionStatus, safeBriefyLink } from '../utils/briefyRuntime';
import {voiceRoute} from '../utils/briefyVoice';
import './BriefyVoice.css';
import {useInterfaceText} from './InterfaceText';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  X, Send, Sparkles, Loader2, RefreshCw, CheckCircle2, AlertCircle, Clock, ArrowRight, Mic, MicOff,
} from 'lucide-react';

const STORAGE_KEY = 'briefy_session_id';

function FabAvatar() {
  const [blink, setBlink] = useState(false);
  useEffect(() => {
    let alive = true;
    let t1;
    let t2;
    const loop = () => {
      t1 = setTimeout(() => {
        if (!alive) return;
        setBlink(true);
        t2 = setTimeout(() => { if (!alive) return; setBlink(false); loop(); }, 170);
      }, 3200 + Math.random() * 3800);
    };
    loop();
    return () => { alive = false; clearTimeout(t1); clearTimeout(t2); };
  }, []);
  return <img src={blink ? '/briefy/blink.png' : '/briefy/base.png'} alt="Briefy" className="briefy-fab-avatar" />;
}

const SR = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

/**
 * BriefyAssistant - global floating AI helper.
 * - Always-on FAB (bottom right) with mascot image
 * - Animated states: idle (breathing), pulse (new tip), open (bounce), typing
 * - Context-aware: detects current route and primes the AI with that context
 * - Multi-language: replies in user's language (ES/EN/PT/FR)
 * - Session-persistent: keeps a session_id in localStorage so conversations continue
 *
 * Backend: POST /api/assistant/chat
 */
export default function BriefyAssistant() {
  const { user } = useAuth();
  const uiText = useInterfaceText();
  const { t, language } = useLanguage();
  const assistantLanguage = language || user?.language || 'es';
  const ui = ({es:{reset:'Nueva conversación',question:'Escribe tu pregunta…',send:'Enviar mensaje'},en:{reset:'New conversation',question:'Type your question…',send:'Send message'},pt:{reset:'Nova conversa',question:'Escreva sua pergunta…',send:'Enviar mensagem'},fr:{reset:'Nouvelle conversation',question:'Écrivez votre question…',send:'Envoyer le message'}})[assistantLanguage] || {reset:'Nueva conversación',question:'Escribe tu pregunta…',send:'Enviar mensaje'};
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const sessionKey = STORAGE_KEY + ':' + (user?.id || user?.email || 'guest');
  const [sessionId, setSessionId] = useState('');
  const lifecycle = useRef(0), activeRequest = useRef(null), currentOwner = useRef(sessionKey);
  currentOwner.current = sessionKey;
  useEffect(() => {
    try { setSessionId(localStorage.getItem(sessionKey) || ''); } catch { setSessionId(''); }
    lifecycle.current++; activeRequest.current = null; sendingRef.current=false;
    setMessages([]); setSuggestions([]); setInput(''); setSending(false); setSpeaking(false);
    return () => { lifecycle.current++; activeRequest.current=null; sendingRef.current=false; };
  }, [sessionKey]);
  const [hasNew, setHasNew] = useState(true); // pulse on first appearance
  const [speaking, setSpeaking] = useState(false);
  const [voiceLocale,setVoiceLocale]=useState(({es:'es-US',en:'en-US',pt:'pt-BR',fr:'fr-FR'})[assistantLanguage]||'es-US');
  useEffect(()=>{setVoiceLocale(({es:'es-US',en:'en-US',pt:'pt-BR',fr:'fr-FR'})[assistantLanguage]||'es-US');},[assistantLanguage]);
  const sendRef=useRef(null),sendingRef=useRef(false);
  const voice=useVoiceInput({language:voiceLocale,onDraft:setInput,onWake:()=>setOpen(true),onCommand:async text=>{
    const route=voiceRoute(text);
    if(route){navigate(route);setMessages(prev=>[...prev,{role:'assistant',text:uiText('Abriendo la herramienta solicitada.')}]);return;}
    await sendRef.current?.(text,true);
  }});
  const listening=voice.listening;
  const closePanel=()=>{voice.closePanel();setOpen(false);};
  const stopListening=()=>voice.stop();
  const startListening=()=>voice.startDictation(input);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.querySelector('[data-testid="briefy-close"]')?.focus();
    const closeOnEscape = e => {
      if (e.key === 'Escape') closePanel();
      if (e.key !== 'Tab') return;
      const nodes = [...(panelRef.current?.querySelectorAll('button:not(:disabled),input:not(:disabled),select:not(:disabled),summary') || [])].filter(n => n.getClientRects().length);
      const first = nodes[0], last = nodes[nodes.length-1];
      if (e.shiftKey && document.activeElement === first) {e.preventDefault();last?.focus();}
      else if (!e.shiftKey && document.activeElement === last) {e.preventDefault();first?.focus();}
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, [open]);

  // Allow other components (e.g. Dashboard Briefy card) to open the panel
  const [pendingPrefill, setPendingPrefill] = useState(null);
  useEffect(() => {
    const openHandler = (e) => {
      setOpen(true);
      if (e?.detail?.prefill) setPendingPrefill(e.detail.prefill);
    };
    window.addEventListener('briefy:open', openHandler);
    return () => window.removeEventListener('briefy:open', openHandler);
  }, []);

  // Auto-send contextual question (e.g. "Habla con BRIEFY sobre este cliente")
  useEffect(() => {
    if (open && pendingPrefill) {
      const text = pendingPrefill;
      setPendingPrefill(null);
      sendMessage(text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingPrefill]);

  // Let BriefyIdle know the chat is open (avoid double-presence)
  useEffect(() => {
    window.__briefyChatOpen = open;
  }, [open]);

  // Hide assistant on public pages (no auth) OR if user disabled it in settings
  const publicRoutes = ['/login', '/register', '/sign/', '/portal/', '/card/', '/landing'];
  const isPublic = !user || publicRoutes.some((r) => location.pathname.startsWith(r));
  const userDisabled = user && user.show_assistant === false;

  useEffect(()=>{if(isPublic||userDisabled)voice.stop();},[isPublic,userDisabled]);

  // Show intro message when first opened
  useEffect(() => {
    if (open && messages.length === 0) {
      const greeting = greetByLanguage(assistantLanguage, user?.name || user?.business_name || '', location.pathname);
      setMessages([{ role: 'assistant', text: greeting.text, greeting:true }]);
      setSuggestions(greeting.suggestions);
      setHasNew(false);
    }
  }, [open, messages.length, sessionKey, assistantLanguage]);

  useEffect(()=>{setMessages(previous=>previous.map(message=>message.greeting?{...message,text:greetByLanguage(assistantLanguage,user?.name||user?.business_name||'',location.pathname).text}:message));},[assistantLanguage]);

  // When the user navigates to a different page WHILE Briefy is open,
  // refresh the suggestions so they match the new tool (without sending a message).
  useEffect(() => {
    if (open) {
      setSuggestions(getRouteSuggestions(assistantLanguage, location.pathname));
    }
  }, [location.pathname, open, assistantLanguage]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const sendMessage = async (textOverride, fromVoice=false) => {
    const text = (typeof textOverride === 'string' ? textOverride : input).trim();
    if (!text || sendingRef.current) return;
    if (text.length > 2000) {
      setMessages(prev => [...prev, {role:'assistant', text:t('briefy.tooLong','El mensaje supera los 2000 caracteres. Divídelo en mensajes más cortos; tu texto se conserva.'), error:true}]);
      return;
    }
    const request = {owner:sessionKey, generation:lifecycle.current};
    activeRequest.current=request; sendingRef.current=true;
    const isCurrent = () => activeRequest.current===request && lifecycle.current===request.generation && currentOwner.current===request.owner;
    if(!fromVoice)voice.stop();
    setInput(''); setSuggestions([]);
    setMessages(prev => [...prev, {role:'user',text}]);
    setSending(true); setSpeaking(true);
    try {
      const response=await api.post('/assistant/chat', {
        message:text, route:location.pathname, language:assistantLanguage,
        timezone:Intl.DateTimeFormat().resolvedOptions().timeZone,
        session_id:sessionId || undefined,
      }, {timeout:120000});
      if(!isCurrent())return;
      const result=parseBriefyResponse(response.data);
      if(result.session_id && result.session_id!==sessionId) {
        setSessionId(result.session_id);
        try {localStorage.setItem(request.owner,result.session_id);}catch {}
      }
      setMessages(prev=>[...prev,{role:'assistant',text:result.reply,actions:result.actions}]);
      setSuggestions(result.suggestions);
    } catch(error) {
      if(!isCurrent())return;
      setInput(text);
      setMessages(prev=>[...prev,{role:'assistant',text:t('briefy.unconfirmed','No pude confirmar la respuesta. Tu texto se conserva. Si pediste guardar algo, revisa la herramienta antes de repetir para evitar duplicados.'),error:true}]);
    } finally {
      if(isCurrent()) {activeRequest.current=null; sendingRef.current=false; setSending(false); setSpeaking(false);}
    }
  };
  sendRef.current=sendMessage;
  const resetSession = () => {
    if(sendingRef.current)return;
    voice.stop(); lifecycle.current++; activeRequest.current=null;
    setSessionId(''); setInput('');
    const greeting=greetByLanguage(assistantLanguage,user?.name || user?.business_name || '',location.pathname);
    setMessages([{role:'assistant',text:greeting.text,greeting:true}]); setSuggestions(greeting.suggestions);
    try {localStorage.removeItem(sessionKey);}catch {}
  };

  if (isPublic || userDisabled) return null;

  return (
    <>
      {/* === Floating Action Button === */}
      <button
        onClick={() => setOpen(true)}
        aria-label={uiText('Abrir asistente BRIEFY')} aria-haspopup="dialog"
        className={`briefy-fab ${hasNew ? 'briefy-fab-pulse' : ''}`}
        data-testid="briefy-fab"
        style={{ display: open ? 'none' : 'flex' }}
      >
        <FabAvatar />
        <span className="briefy-fab-dot" />
      </button>

      {voice.enabled&&!open&&<button type="button" onClick={()=>voice.stop()} className="briefy-voice-badge" aria-label={uiText("Desactivar activación por Briefy")}><Mic size={15}/> {uiText("Di «Briefy» · Apagar")}</button>}
      {/* === Chat Panel === */}
      {open && createPortal(
        <div className="briefy-overlay" data-testid="briefy-panel" onClick={closePanel}>
          <div className="briefy-panel" ref={panelRef} role="dialog" aria-modal="true" aria-label="BRIEFY" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="briefy-header">
              <div className={`briefy-header-avatar ${speaking ? 'briefy-speaking' : ''}`}>
                <img src={speaking ? '/briefy/explaining.png' : '/briefy/base.png'} alt="Briefy, asistente de BRIEFCASE" />
              </div>
              <div className="briefy-header-info">
                <p className="briefy-header-name briefy-name-tooltip" data-testid="briefy-name">
                  Briefy
                  <span className="briefy-tooltip-bubble" role="tooltip">
                    <span className="briefy-tooltip-title">{t('briefy.tooltipTitle', '¿Por qué Briefy?')}</span>
                    <span className="briefy-tooltip-line"><strong>Brief</strong> · {t('briefy.tooltipBrief', 'resúmenes ejecutivos al instante')}</span>
                    <span className="briefy-tooltip-line"><strong>BRIEFCASE</strong> · {t('briefy.tooltipFamily', 'soy parte del maletín')}</span>
                    <span className="briefy-tooltip-line"><strong>Briefly</strong> · {t('briefy.tooltipBriefly', 'breve, rápido, sin fricción')}</span>
                  </span>
                </p>
                <p className="briefy-header-role">
                  <span className="briefy-online-dot" />
                  {speaking ? t('briefy.typing', 'Escribiendo...') : ({es:'Asistente IA · Listo para ayudarte',en:'AI assistant · Ready to help',pt:'Assistente IA · Pronto para ajudar',fr:'Assistant IA · Prêt à vous aider'})[assistantLanguage]}
                </p>
              </div>
              <div className="briefy-header-actions">
                <button onClick={resetSession} disabled={sending} className="briefy-icon-btn" title={ui.reset} aria-label={ui.reset} data-testid="briefy-reset">
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button onClick={closePanel} className="briefy-icon-btn" data-testid="briefy-close" aria-label={t('common.close', 'Cerrar')}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="briefy-messages" role="log" aria-live="polite" aria-busy={sending}>
              {messages.length === 0 && (
                <div className="briefy-empty">
                  <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-300">{t('briefy.startHint', 'Pregúntame cómo usar cualquier herramienta')}</p>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`briefy-msg ${m.role}`}>
                  {m.role === 'assistant' && (
                    <div className="briefy-msg-avatar">
                      <img src="/briefy/base.png" alt="Briefy" />
                    </div>
                  )}
                  <div className="briefy-msg-content">
                    <div className={`briefy-msg-bubble ${m.error ? 'briefy-msg-error' : ''}`}>
                      {m.text.split('\n').map((line, j) => (
                        <p key={j} style={{ margin: 0, marginTop: j > 0 ? '0.4em' : 0 }}>{line}</p>
                      ))}
                    </div>
                    {/* Action chips: shown when Briefy actually executed a tool */}
                    {m.role === 'assistant' && Array.isArray(m.actions) && m.actions.length > 0 && (
                      <div className="briefy-actions" data-testid="briefy-actions">
                        {m.actions.map((a, idx) => {
                          const r = a.result || {};
                          const status=briefyActionStatus(r);
                          const ok=status==='success';
                          const deeplink=safeBriefyLink(r.deeplink);
                          const ActionIcon=ok?CheckCircle2:status==='failed'?AlertCircle:Clock;
                          const statusLabel=ok?t('briefy.confirmed','Confirmado'):status==='failed'?t('briefy.failed','No completado'):t('briefy.pendingConfirmation','Sin confirmar');
                          const labelMap = {
                            create_lead: t('briefy.tool.lead', 'Lead'),
                            create_customer: t('briefy.tool.customer', 'Cliente'),
                            create_contact: t('briefy.tool.contact', 'Contacto'),
                            create_task: t('briefy.tool.task', 'Tarea'),
                            create_appointment: t('briefy.tool.appointment', 'Cita'),
                            save_note:t('briefy.tool.note','Nota'),
                            analyze_price_index:t('briefy.tool.priceIndex','Análisis de precio'),
                            create_service:t('briefy.tool.service','Servicio'),
                            register_service_sale:t('briefy.tool.serviceSale','Venta de servicio'),
                            get_services_report:t('briefy.tool.serviceReport','Reporte de servicios'),
                            search_leads: t('briefy.tool.search', 'Búsqueda'),
                            get_today_summary: t('briefy.tool.summary', 'Resumen'),
                            get_recent_leads: t('briefy.tool.recent', 'Recientes'),
                          };
                          const isWrite = a.tool && a.tool.startsWith('create_');
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                if (deeplink) {
                                  closePanel();
                                  navigate(deeplink);
                                }
                              }}
                              className={`briefy-action-chip ${ok ? 'briefy-action-ok' : 'briefy-action-fail'} ${isWrite ? 'briefy-action-write' : 'briefy-action-read'}`}
                              data-testid={`briefy-action-${a.tool}-${idx}`}
                              aria-label={statusLabel + ": " + (labelMap[a.tool] || a.tool)} title={r.error || r.summary || a.tool}
                            >
                              <ActionIcon className="w-4 h-4" style={{ flexShrink: 0 }} />
                              <span className="briefy-action-label">
                                <strong>{labelMap[a.tool] || a.tool} · {statusLabel}</strong>
                                {r.summary ? ` · ${r.summary}` : (r.error ? ` · ${r.error}` : '')}
                              </span>
                              {deeplink && <ArrowRight className="w-3.5 h-3.5" style={{ flexShrink: 0, opacity: 0.7 }} />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="briefy-msg assistant">
                  <div className="briefy-msg-avatar">
                    <img src="/briefy/base.png" alt="Briefy" />
                  </div>
                  <div className="briefy-msg-bubble">
                    <div className="briefy-dots">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="briefy-accessories">
 <details className="briefy-tools" data-testid="briefy-tools">
 <summary>{t('briefy.exploreTools', 'Explorar herramientas')}</summary>
 <div className="briefy-tools-grid">{BRIEFY_MODULES.map(([route,label]) => <button type="button" key={route} aria-current={location.pathname === route ? 'page' : undefined} onClick={() => { navigate(route); }}>{t('briefy.module.'+route.slice(1),label)}</button>)}</div>
 </details>
 {/* Quick suggestions */}
            {suggestions.length > 0 && !sending && (
              <details className="briefy-suggestion-options" data-testid="briefy-suggestion-options"><summary>{uiText("Preguntas sugeridas")}</summary><div className="briefy-suggestions">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s)}
                    className="briefy-suggestion-chip"
                    data-testid={`briefy-suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div></details>
            )}

            <details className="briefy-voice-controls" data-testid="briefy-voice-options"><summary>{uiText("Opciones de voz")}</summary>
              <div><button type="button" onClick={()=>voice.enabled?voice.stop():voice.enableWake()} disabled={sending||voice.pending} aria-pressed={voice.enabled} data-testid="briefy-wake-toggle">{uiText(voice.enabled?'Apagar activación por nombre':'Activar voz por «Briefy»')}</button>
              <select aria-label={uiText("Idioma del reconocimiento de voz")} value={voiceLocale} onChange={e=>{voice.stop();setVoiceLocale(e.target.value);}}><option value="es-US">Español · América</option><option value="es-ES">Español · España</option><option value="en-US">English</option><option value="pt-BR">Português</option><option value="fr-FR">Français</option></select></div>
              <p role="status">{uiText(voice.pending?'Solicitando acceso al micrófono…':voice.status)}</p>
              <small>{uiText("Actívalo una vez y di «Briefy, abre facturas» o «Briefy, crea una cita…». Solo mientras BRIEFCASE está abierta y visible. El navegador puede procesar el audio con su servicio de voz.")}</small>
            </details>
            </div>
            <p className="briefy-voice-status" role="status">{(listening||voice.pending)&&uiText(voice.pending?"Solicitando acceso al micrófono…":voice.status)}</p>
            {/* Input */}
            <div className="briefy-input-row">
              {SR && (
                <button
                  type="button"
                  onClick={() => listening ? stopListening() : startListening(true)}
                  className={`briefy-mic-btn ${listening ? 'briefy-mic-active' : ''}`}
                  title={listening ? t('briefy.stopVoice', 'Detener dictado') : t('briefy.startVoice', 'Dictar por voz')}
                  data-testid="briefy-mic"
                  disabled={sending||voice.pending}
                >
                  {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
              )}
              <input ref={inputRef} aria-label={ui.question}
                type="text" maxLength={2000}
                value={input}
                onChange={(e) => {voice.stop();setInput(e.target.value);}}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { if (listening) stopListening(); sendMessage(); } }}
                placeholder={listening ? t('briefy.listening', 'Escuchando... habla') : ui.question}
                className="briefy-input"
                data-testid="briefy-input"
                disabled={sending}
              />
              <button
                onClick={() => { stopListening(); sendMessage(); }}
                disabled={!input.trim() || sending}
                className="briefy-send-btn" aria-label={ui.send}
                data-testid="briefy-send"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <style>{BRIEFY_STYLES}</style>
        </div>,
        document.body
      )}

      {/* Mini styles for FAB (always rendered) */}
      <style>{BRIEFY_FAB_STYLES}</style>
    </>
  );
}

// === Route → quick-suggestions catalogue (mirrors backend) ===
// Used for the FIRST greeting so the user sees context-aware chips immediately,
// before sending any message. After each chat reply, the backend overrides this
// with its own (also route-aware) suggestions.
const SUGGESTIONS_BY_ROUTE = {
  es: {
    '/leads': ['¿Cómo enriquecer un lead con IA?', '¿Cómo convertir un lead a cliente?', 'Crea un lead para [Nombre] de [Empresa]', 'Busca leads de [nombre]', 'Mis 5 leads más recientes', '¿Cómo importar leads desde Excel?'],
    '/realestate': ['¿Cómo crear un portal para mi cliente?', '¿Cómo funciona el Tour AI de propiedades?', '¿Cómo geo-buscar propiedades cercanas?'],
    '/pos': ['¿Cómo registrar una venta nueva?', '¿Cómo aplicar un descuento al carrito?', '¿Cómo cerrar la caja del día?'],
    '/cash': ['Anota un ingreso de efectivo', 'Anota un gasto de hoy', '¿Cómo ver el balance del día?'],
    '/payroll': ['¿Cómo calcular impuestos por país?', '¿Cómo agregar un empleado nuevo?', '¿Cómo cambiar el país de cálculo?'],
    '/facturas': ['¿Cómo escanear una factura con OCR?', '¿Cómo enviar una factura para firma?', '¿Cómo crear una factura nueva?'],
    '/inventory': ['¿Cómo agregar un producto nuevo?', '¿Cómo imprimir etiquetas de código de barras?', '¿Cómo ver productos con stock bajo?'],
    '/dashboard': ['Resumen de mis ventas de hoy', 'Mis 5 leads más recientes', '¿Cómo personalizar mi dashboard?', 'Dame un resumen de la semana', '¿Cuántas citas tengo esta semana?', 'Muéstrame los productos top'],
    '/business-card': ['¿Cómo compartir mi tarjeta digital?', '¿Cómo cambiar mi foto y colores?', '¿Cómo agregar mis redes sociales?'],
    '/settings': ['¿Cómo cambiar mi idioma y moneda?', '¿Cómo activar autenticación 2FA?', '¿Cómo cambiar mi contraseña?'],
    '/customers': ['Crea un cliente nuevo', '¿Cómo agendar una cita con un cliente?', '¿Cómo ver el historial de un cliente?', '¿Cómo enviar un mensaje a un cliente?', '¿Cómo exportar mi lista de clientes?', '¿Cómo importar clientes desde Excel?'],
    '/contacts': ['Agrega un contacto nuevo', '¿Cómo importar contactos desde Excel?', '¿Cómo filtrar contactos por categoría?', '¿Cómo enviar mensaje masivo a contactos?', '¿Cómo etiquetar contactos importantes?', '¿Cómo exportar mis contactos?'],
    '/tasks': ['Crea una tarea para mañana', '¿Cómo marcar una tarea como completada?', '¿Cómo activar recordatorios?', '¿Cómo cambiar la fecha de una tarea?', '¿Qué tareas tengo pendientes hoy?', '¿Cómo asignar prioridad a una tarea?'],
    '/notes': ['Apunta esta lista que te voy a dictar', '¿Cómo dictar una nota por voz?', '¿Cómo buscar en mis notas?', '¿Cómo fijar (pin) una nota importante?', '¿Cómo etiquetar mis notas?', '¿Cómo compartir una nota?'],
    '/calendar': ['¿Cómo agendar una cita?', 'Agenda una cita para mañana 10am', '¿Cómo ver las citas de esta semana?'],
    '/companies': ['¿Cómo agregar una empresa nueva?', '¿Cómo vincular contactos a una empresa?', '¿Cómo ver el pipeline por empresa?'],
    '/budget': ['¿Cómo crear un presupuesto mensual?', '¿Cómo categorizar mis gastos?', '¿Cómo ver mi balance vs presupuesto?'],
    '/purchases': ['¿Cómo registrar una compra?', '¿Cómo registrar el pago a un proveedor?', '¿Cómo ver mis compras del mes?'],
    '/administration': ['¿Cómo gestionar permisos de usuarios?', '¿Cómo ver el log de auditoría?', '¿Cómo respaldar mis datos?'],
    '/transport': ['¿Cómo registrar un viaje?', '¿Cómo calcular gastos de combustible?', '¿Cómo ver mis rutas frecuentes?'],
  },
  en: {
    '/leads': ['How to enrich a lead with AI?', 'How to convert a lead to a customer?', 'Create a lead for [Name] from [Company]'],
    '/realestate': ['How to create a portal for my client?', 'How does the AI property tour work?', 'How to geo-search nearby properties?'],
    '/pos': ['How to register a new sale?', 'How to apply a discount?', "How to close the day's register?"],
    '/cash': ['Note a cash income', 'Note an expense today', "How to see today's balance?"],
    '/payroll': ['How to calculate taxes per country?', 'How to add a new employee?', 'How to change the calculation country?'],
    '/facturas': ['How to scan an invoice with OCR?', 'How to send an invoice for signing?', 'How to create a new invoice?'],
    '/inventory': ['How to add a new product?', 'How to print barcode labels?', 'How to see low-stock products?'],
    '/dashboard': ["Summary of today's sales", 'My 5 most recent leads', 'How to customize my dashboard?'],
    '/business-card': ['How to share my digital card?', 'How to change my photo and colors?', 'How to add my social networks?'],
    '/settings': ['How to change language and currency?', 'How to enable 2FA?', 'How to change my password?'],
    '/customers': ['Create a new customer', 'How to schedule an appointment?', "How to view a customer's history?"],
    '/contacts': ['Add a new contact', 'How to import contacts from Excel?', 'How to filter by category?'],
    '/tasks': ['Create a task for tomorrow', 'How to mark a task as completed?', 'How to enable reminders?'],
    '/notes': ["Take this list I'm about to dictate", 'How to dictate a note by voice?', 'How to search my notes?'],
    '/calendar': ['How to schedule an appointment?', 'Schedule appointment tomorrow 10am', "How to see this week's appointments?"],
    '/companies': ['How to add a new company?', 'How to link contacts to a company?', 'How to see pipeline by company?'],
    '/budget': ['How to create a monthly budget?', 'How to categorize my expenses?', 'How to see my balance vs budget?'],
    '/purchases': ['How to register a purchase?', 'How to register a vendor payment?', "How to see this month's purchases?"],
    '/administration': ['How to manage user permissions?', 'How to see the audit log?', 'How to back up my data?'],
    '/transport': ['How to register a trip?', 'How to calculate fuel expenses?', 'How to see frequent routes?'],
  },
  pt: {
    '/leads': ['Como enriquecer um lead com IA?', 'Como converter um lead em cliente?', 'Crie um lead para [Nome] de [Empresa]'],
    '/realestate': ['Como criar um portal para meu cliente?', 'Como funciona o Tour AI de imóveis?', 'Como geo-buscar imóveis próximos?'],
    '/pos': ['Como registrar uma nova venda?', 'Como aplicar desconto no carrinho?', 'Como fechar o caixa do dia?'],
    '/cash': ['Anote uma receita em dinheiro', 'Anote uma despesa de hoje', 'Como ver o saldo do dia?'],
    '/payroll': ['Como calcular impostos por país?', 'Como adicionar um novo funcionário?', 'Como mudar o país de cálculo?'],
    '/facturas': ['Como escanear uma fatura com OCR?', 'Como enviar uma fatura para assinatura?', 'Como criar uma nova fatura?'],
    '/inventory': ['Como adicionar um produto?', 'Como imprimir etiquetas de código de barras?', 'Como ver produtos com estoque baixo?'],
    '/dashboard': ['Resumo das minhas vendas de hoje', 'Meus 5 leads mais recentes', 'Como personalizar meu dashboard?'],
    '/business-card': ['Como compartilhar meu cartão digital?', 'Como mudar minha foto e cores?', 'Como adicionar minhas redes sociais?'],
    '/settings': ['Como mudar idioma e moeda?', 'Como ativar 2FA?', 'Como mudar minha senha?'],
    '/customers': ['Crie um cliente novo', 'Como agendar um compromisso?', 'Como ver o histórico de um cliente?'],
    '/contacts': ['Adicione um contato novo', 'Como importar contatos do Excel?', 'Como filtrar contatos por categoria?'],
    '/tasks': ['Crie uma tarefa para amanhã', 'Como marcar uma tarefa como concluída?', 'Como ativar lembretes?'],
    '/notes': ['Anote esta lista que vou ditar', 'Como ditar uma nota por voz?', 'Como buscar nas minhas notas?'],
    '/calendar': ['Como agendar um compromisso?', 'Agende compromisso amanhã 10h', 'Como ver compromissos desta semana?'],
    '/companies': ['Como adicionar uma empresa nova?', 'Como vincular contatos a uma empresa?', 'Como ver o pipeline por empresa?'],
    '/budget': ['Como criar um orçamento mensal?', 'Como categorizar minhas despesas?', 'Como ver meu saldo vs orçamento?'],
    '/purchases': ['Como registrar uma compra?', 'Como registrar pagamento a fornecedor?', 'Como ver minhas compras do mês?'],
    '/administration': ['Como gerenciar permissões de usuários?', 'Como ver o log de auditoria?', 'Como fazer backup dos meus dados?'],
    '/transport': ['Como registrar uma viagem?', 'Como calcular gastos de combustível?', 'Como ver minhas rotas frequentes?'],
  },
  fr: {
    '/leads': ["Comment enrichir un lead avec l'IA ?", 'Comment convertir un lead en client ?', 'Créez un lead pour [Nom] de [Entreprise]'],
    '/realestate': ['Comment créer un portail pour mon client ?', 'Comment fonctionne le Tour IA ?', 'Comment géo-rechercher des propriétés ?'],
    '/pos': ['Comment enregistrer une vente ?', 'Comment appliquer une remise ?', 'Comment clôturer la caisse du jour ?'],
    '/cash': ['Notez une recette en espèces', "Notez une dépense d'aujourd'hui", 'Comment voir le solde du jour ?'],
    '/payroll': ['Comment calculer les impôts par pays ?', 'Comment ajouter un employé ?', 'Comment changer le pays ?'],
    '/facturas': ['Comment scanner une facture avec OCR ?', 'Comment envoyer une facture pour signature ?', 'Comment créer une nouvelle facture ?'],
    '/inventory': ['Comment ajouter un produit ?', 'Comment imprimer des étiquettes ?', 'Comment voir les produits en stock bas ?'],
    '/dashboard': ["Résumé de mes ventes d'aujourd'hui", 'Mes 5 leads les plus récents', 'Comment personnaliser mon tableau de bord ?'],
    '/business-card': ['Comment partager ma carte numérique ?', 'Comment changer ma photo et mes couleurs ?', 'Comment ajouter mes réseaux sociaux ?'],
    '/settings': ['Comment changer langue et devise ?', "Comment activer l'authentification 2FA ?", 'Comment changer mon mot de passe ?'],
    '/customers': ['Créez un nouveau client', 'Comment planifier un rendez-vous ?', "Comment voir l'historique d'un client ?"],
    '/contacts': ['Ajoutez un nouveau contact', 'Comment importer des contacts depuis Excel ?', 'Comment filtrer par catégorie ?'],
    '/tasks': ['Créez une tâche pour demain', 'Comment marquer une tâche comme terminée ?', 'Comment activer les rappels ?'],
    '/notes': ['Notez cette liste que je vais dicter', 'Comment dicter une note par la voix ?', 'Comment chercher dans mes notes ?'],
    '/calendar': ['Comment planifier un rendez-vous ?', 'Rendez-vous demain 10h', 'Comment voir les rendez-vous de cette semaine ?'],
    '/companies': ['Comment ajouter une entreprise ?', 'Comment lier des contacts à une entreprise ?', 'Comment voir le pipeline par entreprise ?'],
    '/budget': ['Comment créer un budget mensuel ?', 'Comment catégoriser mes dépenses ?', 'Comment voir mon solde vs budget ?'],
    '/purchases': ['Comment enregistrer un achat ?', 'Comment enregistrer le paiement à un fournisseur ?', 'Comment voir mes achats du mois ?'],
    '/administration': ['Comment gérer les permissions ?', "Comment voir le journal d'audit ?", 'Comment sauvegarder mes données ?'],
    '/transport': ['Comment enregistrer un trajet ?', 'Comment calculer les frais de carburant ?', 'Comment voir mes itinéraires fréquents ?'],
  },
};

const DEFAULT_SUGGESTIONS = {
  es: ['¿Qué puede hacer BRIEFCASE por mí?', '¿Cómo conecto mi banco o terminal?', 'Dame un tour rápido de la app'],
  en: ['What can BRIEFCASE do for me?', 'How to connect my bank or POS?', 'Give me a quick tour'],
  pt: ['O que BRIEFCASE pode fazer por mim?', 'Como conecto meu banco?', 'Me dê um tour rápido'],
  fr: ['Que peut faire BRIEFCASE pour moi ?', 'Comment connecter ma banque ?', 'Faites-moi un tour rapide'],
};

// Pick 3 random items from the pool (no deps)
function pickRandomThree(pool) {
  if (!pool || pool.length <= 3) return [...(pool || [])];
  const copy = [...pool];
  const out = [];
  for (let i = 0; i < 3; i++) {
    const idx = Math.floor(Math.random() * copy.length);
    out.push(copy.splice(idx, 1)[0]);
  }
  return out;
}

function getRouteSuggestions(lang, pathname) {
  pathname = pathname.replace(/^\/appointments/, '/calendar').replace(/^\/admin(?=\/|$)/, '/administration');
  const dict = SUGGESTIONS_BY_ROUTE[lang] || SUGGESTIONS_BY_ROUTE.es;
  const defaults = DEFAULT_SUGGESTIONS[lang] || DEFAULT_SUGGESTIONS.es;
  let best = defaults;
  let bestLen = 0;
  for (const [prefix, list] of Object.entries(dict)) {
    if (pathname.startsWith(prefix) && prefix.length > bestLen) {
      best = list;
      bestLen = prefix.length;
    }
  }
  if (!bestLen) {
    const module = BRIEFY_MODULES.find(([route]) => pathname.startsWith(route));
    if (module && lang === 'es') return ['¿Cómo uso ' + module[1] + '?', ...defaults].slice(0,3);
  }
  return pickRandomThree(best);
}

function greetByLanguage(lang, name, pathname = '/') {
  const greetings = {
    es: `¡Hola${name ? ' ' + name.split(' ')[0] : ''}! 👋 Soy Briefy, tu asistente. Pregúntame cómo usar cualquier herramienta o pídeme que ejecute acciones (crear leads, citas, notas dictadas, etc.).`,
    en: `Hi${name ? ' ' + name.split(' ')[0] : ''}! 👋 I'm Briefy, your assistant. Ask me how to use any tool or ask me to execute actions (create leads, appointments, voice notes, etc.).`,
    pt: `Olá${name ? ' ' + name.split(' ')[0] : ''}! 👋 Sou Briefy, seu assistente. Pergunte-me como usar qualquer ferramenta ou peça que eu execute ações (criar leads, compromissos, notas, etc.).`,
    fr: `Bonjour${name ? ' ' + name.split(' ')[0] : ''}! 👋 Je suis Briefy, votre assistant. Demandez-moi comment utiliser un outil ou demandez-moi d'exécuter des actions (créer leads, rendez-vous, notes, etc.).`,
  };
  return {
    text: greetings[lang] || greetings.es,
    suggestions: getRouteSuggestions(lang || 'es', pathname),
  };
}

const BRIEFY_FAB_STYLES = `
  .briefy-fab {
    position: fixed;
    right: 18px;
    bottom: 18px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #ffffff;
    border: 2px solid transparent;
    background: linear-gradient(white,white) padding-box, linear-gradient(135deg,#2563EB,#7C3AED,#06B6D4) border-box;
    cursor: pointer;
    z-index: 9998;
    box-shadow: 0 8px 24px -8px rgba(15,48,87,0.4), 0 0 0 1px rgba(15,48,87,0.1);
    align-items: center;
    justify-content: center;
    padding: 4px;
    overflow: visible;
    transition: transform 0.2s ease;
  }
  .briefy-fab:hover {
    transform: scale(1.08) translateY(-2px);
    box-shadow: 0 12px 30px -8px rgba(15,48,87,0.5);
  }
  .briefy-fab:active { transform: scale(0.95); }
  .briefy-fab-avatar {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 50%;
    background: white;
    animation: briefyFloat 3s ease-in-out infinite;
  }
  .briefy-fab-dot {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 12px;
    height: 12px;
    background: #22c55e;
    border: 2px solid white;
    border-radius: 50%;
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.6);
    animation: briefyPing 2s ease-in-out infinite;
  }
  .briefy-fab-pulse {
    animation: briefyPulseRing 2.5s ease-in-out infinite;
  }
  @keyframes briefyBreathe {
    0%, 100% { transform: scale(1); }
    50%      { transform: scale(1.04); }
  }
  @keyframes briefyFloat {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-2px); }
  }
  @keyframes briefyPing {
    0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.6); }
    50%      { box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); }
  }
  @keyframes briefyPulseRing {
    0%, 100% { box-shadow: 0 8px 24px -8px rgba(15,48,87,0.4), 0 0 0 1px rgba(15,48,87,0.1), 0 0 0 0 rgba(15,48,87,0.4); }
    50%      { box-shadow: 0 8px 24px -8px rgba(15,48,87,0.4), 0 0 0 1px rgba(15,48,87,0.1), 0 0 0 12px rgba(15,48,87,0); }
  }
  @media (max-width: 768px) {
    .briefy-fab { width: 50px; height: 50px; right: 14px; bottom: 82px; }
  }
  @media (prefers-reduced-motion: reduce) {
    .briefy-fab, .briefy-fab-avatar, .briefy-fab-dot, .briefy-fab-pulse { animation: none !important; }
  }
`;

const BRIEFY_STYLES = `
  .briefy-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    pointer-events: none;
    display: flex;
    align-items: stretch;
    justify-content: flex-end;
    padding: 0;
    background: rgba(15, 23, 42, 0.25);
    backdrop-filter: blur(2px);
    pointer-events: auto;
  }
  .briefy-panel {
    pointer-events: auto;
    width: min(420px, 100vw);
    height: 100vh; height: 100dvh;
    background: #ffffff;
    border: none;
    border-left: 1px solid #E2E8F0;
    border-top: 2px solid #2563EB;
    border-radius: 0;
    box-shadow: -28px 0 70px -15px rgba(8, 17, 40, 0.5);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: briefyPanelIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  @keyframes briefyPanelIn {
    from { opacity: 0; transform: translateX(40px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .briefy-header {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: linear-gradient(135deg, #0F1B36 0%, #152849 60%, #0F1B36 100%);
    border-bottom: 1px solid rgba(212,175,55,0.35);
  }
  .briefy-header::after {
    content: '';
    position: absolute;
    left: 0; right: 0; bottom: -1px;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(212,175,55,0.95), transparent);
    animation: cinematicGlowPulse 3.2s ease-in-out infinite;
  }
  .briefy-header-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid rgba(212,175,55,0.7);
    background: #ffffff;
    flex-shrink: 0;
    animation: briefyFloat 3s ease-in-out infinite;
  }
  .briefy-header-avatar img { width: 100%; height: 100%; object-fit: cover; object-position: center 18%; }
  .briefy-speaking { animation: briefyShake 0.6s ease-in-out infinite, briefyFloat 3s ease-in-out infinite; }
  @keyframes briefyShake {
    0%, 100% { transform: rotate(0); }
    25%      { transform: rotate(-3deg); }
    75%      { transform: rotate(3deg); }
  }
  .briefy-header-info { flex: 1; min-width: 0; }
  .briefy-header-name {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 17px;
    font-weight: 700;
    color: #fde68a;
    margin: 0;
    line-height: 1.2;
    letter-spacing: 0.02em;
  }
  /* === Name tooltip (hover reveals meaning of "Briefy") === */
  .briefy-name-tooltip {
    position: relative;
    display: inline-block;
    cursor: help;
    border-bottom: 1px dotted rgba(253, 230, 138, 0.4);
    padding-bottom: 1px;
  }
  .briefy-tooltip-bubble {
    position: absolute;
    top: calc(100% + 10px);
    left: 0;
    width: 260px;
    background: #F8FAFC;
    color: #0F1B36;
    border: 1px solid rgba(212, 175, 55, 0.5);
    border-radius: 14px;
    padding: 14px 16px;
    box-shadow: 0 16px 40px -12px rgba(15, 27, 54, 0.45), 0 4px 12px rgba(0,0,0,0.18);
    opacity: 0;
    visibility: hidden;
    transform: translateY(-4px);
    transition: opacity 0.22s ease, transform 0.22s ease, visibility 0.22s ease;
    pointer-events: none;
    z-index: 10;
    font-family: 'Inter', system-ui, sans-serif;
    letter-spacing: normal;
    text-align: left;
  }
  .briefy-tooltip-bubble::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 24px;
    width: 12px;
    height: 12px;
    background: #ffffff;
    border-top: 1px solid rgba(212, 175, 55, 0.5);
    border-left: 1px solid rgba(212, 175, 55, 0.5);
    transform: rotate(45deg);
  }
  .briefy-name-tooltip:hover .briefy-tooltip-bubble,
  .briefy-name-tooltip:focus-within .briefy-tooltip-bubble {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
  .briefy-tooltip-title {
    display: block;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #78350f;
    margin-bottom: 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid rgba(212, 175, 55, 0.3);
  }
  .briefy-tooltip-line {
    display: block;
    font-size: 12px;
    line-height: 1.55;
    color: #334155;
    margin-top: 6px;
    font-weight: 400;
  }
  .briefy-tooltip-line strong {
    color: #0F1B36;
    font-weight: 700;
  }
  .briefy-header-role {
    font-size: 11px;
    color: #cbd5e1;
    margin: 2px 0 0;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .briefy-online-dot {
    width: 7px;
    height: 7px;
    background: #22c55e;
    border-radius: 50%;
    animation: briefyPing 2s ease-in-out infinite;
  }
  .briefy-header-actions { display: flex; gap: 4px; }
  .briefy-icon-btn {
    background: rgba(255,255,255,0.1);
    border: none;
    color: #fde68a;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .briefy-icon-btn:hover { background: rgba(212,175,55,0.25); color: white; }

  .briefy-messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px 14px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    scrollbar-width: thin;
    background:
      radial-gradient(ellipse at 20% 10%, rgba(212,175,55,0.06), transparent 50%),
      radial-gradient(ellipse at 90% 90%, rgba(15,27,54,0.04), transparent 50%);
  }
  .briefy-messages::-webkit-scrollbar { width: 4px; }
  .briefy-messages::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.4); border-radius: 4px; }

  .briefy-empty {
    text-align: center;
    padding: 30px 16px;
    color: #475569;
  }

  .briefy-msg {
    display: flex;
    gap: 10px;
    align-items: flex-end;
    animation: briefyMsgIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  .briefy-msg.user { flex-direction: row-reverse; }
  @keyframes briefyMsgIn {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .briefy-msg-avatar {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    border: 2px solid rgba(212,175,55,0.55);
    background: #ffffff;
    align-self: flex-end;
    box-shadow: 0 4px 12px -4px rgba(15,27,54,0.18);
  }
  .briefy-msg-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 18%;
  }
  .briefy-msg-content {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-width: 78%;
  }
  .briefy-msg.user .briefy-msg-content { align-items: flex-end; }
  .briefy-msg.user .briefy-msg-bubble {
    background: linear-gradient(135deg, #0F1B36 0%, #152849 100%);
    color: #ffffff;
    border: 1px solid rgba(212,175,55,0.3);
    border-bottom-right-radius: 4px;
    border-bottom-left-radius: 16px;
    font-weight: 500;
  }
  .briefy-msg-bubble {
    padding: 11px 15px;
    border-radius: 16px;
    font-size: 14.5px;
    line-height: 1.55;
    color: #1a1a1a;
    background: #ffffff;
    border: 1px solid rgba(15, 27, 54, 0.12);
    border-bottom-left-radius: 4px;
    box-shadow: 0 4px 14px -8px rgba(15,27,54,0.25);
    font-weight: 500;
    letter-spacing: 0.005em;
  }
  .briefy-msg-error {
    background: #fef2f2;
    border-color: rgba(239,68,68,0.4);
    color: #991b1b;
  }

  /* === Action chips (function-calling results) === */
  .briefy-actions {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 2px;
  }
  .briefy-action-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 13px;
    border-radius: 12px;
    font-size: 12.5px;
    line-height: 1.35;
    cursor: pointer;
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    color: #78350f;
    border: 1px solid rgba(212,175,55,0.55);
    text-align: left;
    transition: all 0.18s ease;
    font-family: inherit;
    font-weight: 600;
    animation: briefyActionIn 0.4s cubic-bezier(0.16,1,0.3,1) both;
    box-shadow: 0 2px 8px -2px rgba(212,175,55,0.25);
  }
  .briefy-action-chip:hover {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    color: #422006;
    transform: translateY(-1px);
    box-shadow: 0 6px 18px -6px rgba(212,175,55,0.5);
  }
  .briefy-action-write { border-color: rgba(212,175,55,0.7); }
  .briefy-action-read {
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    color: #1e3a8a;
    border-color: rgba(96,165,250,0.6);
    box-shadow: 0 2px 8px -2px rgba(96,165,250,0.25);
  }
  .briefy-action-read:hover {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    color: #0c1f5a;
    box-shadow: 0 6px 18px -6px rgba(96,165,250,0.5);
  }
  .briefy-action-fail {
    background: #fef2f2;
    border-color: rgba(239,68,68,0.5);
    color: #991b1b;
  }
  .briefy-action-fail:hover {
    background: #fee2e2;
    color: #7f1d1d;
  }
  .briefy-action-label {
    flex: 1;
    min-width: 0;
    white-space: normal;
    overflow: hidden;
  }
  .briefy-action-label strong {
    text-transform: uppercase;
    font-size: 10.5px;
    letter-spacing: 0.06em;
    margin-right: 4px;
    font-weight: 700;
  }
  @keyframes briefyActionIn {
    from { opacity: 0; transform: translateY(6px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .briefy-dots { display: flex; gap: 4px; padding: 4px 0; }
  .briefy-dots span {
    width: 7px;
    height: 7px;
    background: #2563EB;
    border-radius: 50%;
    animation: briefyDotJump 1.2s ease-in-out infinite;
  }
  .briefy-dots span:nth-child(2) { animation-delay: 0.15s; }
  .briefy-dots span:nth-child(3) { animation-delay: 0.3s; }
  @keyframes briefyDotJump {
    0%, 80%, 100% { transform: scale(0.7); opacity: 0.5; }
    40%           { transform: scale(1); opacity: 1; }
  }

  .briefy-suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 0 14px 10px;
    background: linear-gradient(180deg, transparent, rgba(255,255,255,0.6));
  }
  .briefy-suggestion-chip {
    background: #ffffff;
    border: 1px solid rgba(212,175,55,0.5);
    color: #78350f;
    padding: 7px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 6px -2px rgba(212,175,55,0.2);
  }
  .briefy-suggestion-chip:hover {
    background: #fef3c7;
    color: #422006;
    transform: translateY(-1px);
    border-color: rgba(212,175,55,0.8);
  }

  .briefy-input-row {
    display: flex;
    gap: 8px;
    padding: 12px;
    border-top: 1px solid rgba(15,27,54,0.12);
    background: #ffffff;
  }
  .briefy-input {
    flex: 1;
    background: #F1F5F9;
    border: 1px solid rgba(15,27,54,0.18);
    border-radius: 14px;
    padding: 10px 14px;
    color: #0F1B36;
    font-size: 14.5px;
    font-weight: 500;
    outline: none;
    transition: border-color 0.2s ease, background 0.2s ease;
  }
  .briefy-input:focus {
    border-color: rgba(212,175,55,0.7);
    background: #ffffff;
  }
  .briefy-input::placeholder { color: #94a3b8; }
  .briefy-send-btn {
    background: linear-gradient(135deg, #2563EB, #7C3AED);
    color: #1a1a1a;
    border: none;
    border-radius: 14px;
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
  .briefy-send-btn:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 14px rgba(212,175,55,0.4);
  }
  .briefy-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .briefy-mic-btn {
    background: #F1F5F9;
    color: #0F1B36;
    border: 1px solid rgba(212,175,55,0.5);
    border-radius: 14px;
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
  .briefy-mic-btn:hover:not(:disabled) {
    background: #fef3c7;
    color: #422006;
    transform: scale(1.05);
  }
  .briefy-mic-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .briefy-mic-active {
    background: linear-gradient(135deg, #ef4444, #dc2626) !important;
    color: white !important;
    border-color: rgba(239,68,68,0.7) !important;
    box-shadow: 0 0 0 0 rgba(239,68,68,0.5);
    animation: briefyMicPulse 1.4s ease-in-out infinite;
  }
  @keyframes briefyMicPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.6); }
    50%      { box-shadow: 0 0 0 10px rgba(239,68,68,0); }
  }

  @media (max-width: 768px) {
    .briefy-overlay { padding: 0; align-items: flex-end; justify-content: stretch; }
    .briefy-panel {
      width: 100%;
      height: 85vh; height: 85dvh;
      max-height: 85vh; height: 85dvh;
      border-radius: 20px 20px 0 0;
      border-left: none;
      border-right: none;
      border-bottom: none;
      animation: briefySheetIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) both;
    }
  }
  @keyframes briefySheetIn {
    from { opacity: 0.6; transform: translateY(60px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const BRIEFY_MODULES = [["/dashboard", "Resumen"], ["/pos", "Punto de venta"], ["/inventory", "Inventario"], ["/services", "Servicios"], ["/cash", "Caja"], ["/cobros", "Cobros"], ["/sales-history", "Ventas"], ["/reports", "Reportes"], ["/leads", "Leads"], ["/customers", "Clientes"], ["/contacts", "Contactos"], ["/appointments", "Citas"], ["/realestate", "Propiedades y Gestión"], ["/companies", "Empresas"], ["/facturas", "Facturas"], ["/bills", "Cuentas"], ["/tasks", "Tareas"], ["/notes", "Notas"], ["/payroll", "Nómina"], ["/budget", "Presupuesto"], ["/purchases", "Compras"], ["/transport", "Transporte"], ["/business-card", "Tarjeta digital"], ["/settings", "Configuración"], ["/services#archivador", "Formatos y expedientes"], ["/chat", "Comunicación"], ["/finanzas", "Finanzas"], ["/finanzas/flujo-caja", "Flujo de caja"], ["/finanzas/metas", "Metas de ventas"], ["/finanzas/gastos", "Análisis de gastos"], ["/finanzas/comparativo", "Comparativo"], ["/finanzas/rentabilidad", "Rentabilidad"], ["/finanzas/proyecciones", "Proyecciones"], ["/finanzas/analisis", "Análisis financiero"], ["/price-index", "Índice de precios"], ["/news", "Noticias"]];
